import { Button, Flex, Group, Paper, Tabs, Text, Title, useMantineTheme } from "@mantine/core";
import { FaFilter } from "react-icons/fa";
import PushNotificationEvents from "../enums/PushNotificationEvents";
import { useEffect, useState } from "react";
import { User } from "../models/User";
import { UserFilter } from "../models/UserFilter";
import { DeviceTypes } from "../enums/DeviceTypes";
import { PushNotificationRequest } from "../models/PushNotificationRequest";
import { getUsersWithFilters } from "../services/UserService";
import { sendPushNotification } from "../services/NotificationService";
import { IoPeople } from "react-icons/io5";
import { UserFilterForm } from "./UserFilterForm";
import { PushNotificationForm } from "./PushNotificationForm";
import { UserIdEmailForm } from "./UserIdEmailForm";
import { PushNotificationConfirmation } from "./PushNotificationConfirmation";
import { UserList } from "./UserList";

export const SendPushNotificationsTab = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<UserFilter>({
    deviceType: DeviceTypes.IOS,
    deviceVersion: null,
    emailRequired: false,
    interviewsRequired: false,
    userIds: null,
    emails: null,
    appName: null,
    pageNum: 0,
    pageSize: 50,
  });
  const [notification, setNotification] = useState<PushNotificationRequest>({
    title: "",
    body: "",
    notificationEvent: PushNotificationEvents.CUSTOM,
    webLink: "",
    usersFilter: filters, // Use filters for user-related data
  });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterApplied, setFilterApplied] = useState<boolean>(false);
  const [confirmSend, setConfirmSend] = useState<boolean>(false);
  const [notificationSuccess, setNotificationSuccess] =
    useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>(
    "Something went wrong",
  );
  const [formInvalid, setFormInvalid] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [notificationMode, setNotificationMode] = useState<string>('filters');

  const theme = useMantineTheme();

  const isNullOrEmpty = (arr: string[] | null | undefined): boolean =>
    !arr || arr.length === 0;

  const fieldsInvalid = (): string[] => {
    const invalidFieldsList: string[] = [];
    const urlRegex = new RegExp(
      "^(https?://)(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{2,63}\\b([-a-zA-Z0-9()@:%_\\+.~#?&/=]*)$",
    );

    if (filterApplied && notification.title === "") {
      invalidFieldsList.push("title");
    }
    if (filterApplied && notification.body === "") {
      invalidFieldsList.push("body");
    }
    if (
      filterApplied &&
      notification.notificationEvent === PushNotificationEvents.WEB_LINK &&
      !urlRegex.test(notification.webLink)
    ) {
      invalidFieldsList.push("web url");
    }
    if (
      !filterApplied &&
      notificationMode === 'ids' &&
      isNullOrEmpty(filters.userIds) &&
      isNullOrEmpty(filters.emails)
    ) {
      invalidFieldsList.push("user ids / emails")
    }

    setInvalidFields(invalidFieldsList);
    setFormInvalid(invalidFieldsList.length > 0);

    return invalidFieldsList;
  };

  const handleSend = async () => {
    if (fieldsInvalid().length === 0) {
      setNotification({ ...notification, usersFilter: filters });
      setConfirmSend(true);
    }
  };

  const onSearch = async () => {
    if (fieldsInvalid().length === 0) {
      try {
        setLoading(true);
        const userList: User[] = await getUsersWithFilters(
          setLoading,
          filters,
          setTotalPages,
          setTotalUsers,
        );
        setUsers(userList);
        setFilterApplied(true);
      } catch (error: any) {
        console.error(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const message = await sendPushNotification(setLoading, notification);

      setSuccessMessage(message);
      setFilterApplied(false);
      setConfirmSend(false);
    } catch (error: any) {
      console.error("Error sending push notifications:", error.message);
    } finally {
      setLoading(false);
      setNotificationSuccess(true);
    }
  };

  const setPageNum = (newPageNum: number) => {
    setFilters({ ...filters, pageNum: newPageNum });
  };

  useEffect(() => {
    if (filterApplied) {
      onSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNum]);

  const renderPushNotificationForms = () => {
    if (filterApplied && !confirmSend && !notificationSuccess) {
      return (
        <PushNotificationForm
          setFilterApplied={setFilterApplied}
          notification={notification}
          setNotification={setNotification}
          formInvalid={formInvalid}
          setFormInvalid={setFormInvalid}
          invalidFields={invalidFields}
          setInvalidFields={setInvalidFields}
        />
      );
    }

    if (!filterApplied && !confirmSend && !notificationSuccess) {
      return notificationMode === 'filters' ? (
        <UserFilterForm filters={filters} setFilters={setFilters} />
      ) : (
        <UserIdEmailForm 
          filters={filters} 
          setFilters={setFilters}
          formInvalid={formInvalid}
          invalidFields={invalidFields}
        />
      );
    }

    if (filterApplied && confirmSend && !notificationSuccess) {
      return (
        <PushNotificationConfirmation
          totalUsers={totalUsers}
          notification={notification}
        />
      );
    }

    return <Title order={3}>{successMessage} - please refresh the page.</Title>;
  };

  const renderActionButtons = () => {
    if (filterApplied && !confirmSend && !notificationSuccess) {
      return (
        <Button onClick={handleSend} disabled={users.length < 1}>
          Send Push Notifications
        </Button>
      );
    }

    if (!filterApplied && !confirmSend && !notificationSuccess) {
      return (
        <Button
          onClick={onSearch}
          title={
            "Select both a start date and an end date to enable searching."
          }
        >
          <Text c="white" span>
            Apply
          </Text>
        </Button>
      );
    }

    if (filterApplied && confirmSend && !notificationSuccess) {
      return (
        <Group gap="xl">
          <Button
            w={150}
            size="lg"
            bg={theme.colors.red[7]}
            onClick={() => setConfirmSend(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button w={150} size="lg" onClick={handleConfirm} disabled={loading}>
            Send
          </Button>
        </Group>
      );
    }

    return <Title order={3}>{successMessage} - please refresh the page.</Title>;
  };

  const tabConfig = [
    {
      value: 'filters',
      label: 'Filters',
      icon: <FaFilter size={16} />,
      disabled: filterApplied && notificationMode === 'ids',
    },
    {
      value: 'ids',
      label: 'User IDs / Emails',
      icon: <IoPeople size={16} />,
      disabled: filterApplied && notificationMode === 'filters',
    },
  ];

  return (
    <Flex direction="column" w="100%">
      <Paper shadow="xl" radius="md" p="lg" my="md">
        <Flex direction="column" justify="center" align="center" gap="sm">
          <Tabs
            value={notificationMode}
            onChange={(newValue) => setNotificationMode(newValue ?? 'filters')}
          >
            <Tabs.List>
              {tabConfig.map(({ value, label, icon, disabled }) => (
                <Tabs.Tab
                  key={value}
                  value={value}
                  leftSection={icon}
                  disabled={disabled}
                >
                  {label}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {tabConfig.map(({ value }) => (
              <Tabs.Panel key={value} value={value} pt="md">
                {renderPushNotificationForms()}
              </Tabs.Panel>
            ))}
          </Tabs>

          {renderActionButtons()}
        </Flex>
      </Paper>

      <UserList
        filterApplied={filterApplied}
        filters={filters}
        setPageNum={setPageNum}
        totalPages={totalPages}
        users={users}
        totalUsers={totalUsers}
        loading={loading}
      />
    </Flex>
  );
};
